import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TRIP } from "@/data/content";

function calc(target) {
  const now = new Date().getTime();
  const diff = target - now;
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true };
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const m = Math.floor((diff / (1000 * 60)) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s, done: false };
}

export default function Countdown() {
  const target = new Date(TRIP.startDate).getTime();
  const endTarget = new Date(TRIP.endDate).getTime();
  const [t, setT] = useState(() => calc(target));
  const [now, setNow] = useState(() => new Date().getTime());

  useEffect(() => {
    const id = setInterval(() => {
      setT(calc(target));
      setNow(new Date().getTime());
    }, 1000);
    return () => clearInterval(id);
  }, [target]);

  const journeyStarted = t.done && now < endTarget;
  const journeyCompleted = now >= endTarget;

  const Box = ({ value, label, testid }) => (
    <div
      data-testid={testid}
      className="card-elevated rounded-lg px-4 py-5 md:px-6 md:py-7 text-center min-w-[78px] md:min-w-[110px]"
    >
      <div className="font-serif text-3xl md:text-5xl text-navy tabular-nums">
        {String(value).padStart(2, "0")}
      </div>
      <div className="overline mt-1.5 text-[10px] md:text-xs">{label}</div>
    </div>
  );

  return (
    <section data-testid="countdown-section" className="bg-[var(--maqam-surface)] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="overline">
            {journeyCompleted ? "Alhamdulillah" : journeyStarted ? "In Progress" : "Next Journey"}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-navy mt-3 leading-[1.05] tracking-tight">
            {TRIP.title}
          </h2>
          <div className="mt-4 flex items-center gap-3">
            <span className="gold-divider" />
            <span className="text-sm tracking-wide text-[var(--maqam-muted)]">
              {TRIP.dateRange} · {TRIP.duration}
            </span>
          </div>
          <p className="mt-6 text-[var(--maqam-muted)] leading-relaxed max-w-md">
            {journeyCompleted
              ? "The October 2026 journey has concluded — alhamdulillah for a blessed pilgrimage. Register your interest for our next trip and travel with us."
              : journeyStarted
              ? "The journey has begun — our pilgrims are with us in the Haramain. Please keep them in your du‘ā."
              : "A guided journey with knowledge, community, and care — from the moment you arrive at the airport until you return home."}
          </p>
          <Link
            to={journeyCompleted ? "/#interest" : "/umrah-october-2026"}
            data-testid="next-journey-details-btn"
            className="mt-8 inline-flex items-center gap-2 btn-primary rounded-full px-6 py-3 font-semibold"
          >
            {journeyCompleted ? "Register Interest for Next Journey" : "View October 2026 Trip Details"} <ArrowRight size={18} />
          </Link>
        </div>
        <div className="lg:col-span-7">
          {journeyStarted ? (
            <div
              data-testid="countdown-in-progress"
              className="card-elevated rounded-2xl px-8 py-10 md:px-12 md:py-14 text-center max-w-md ml-auto"
            >
              <div className="font-serif text-3xl md:text-4xl text-navy leading-tight">
                The journey has begun.
              </div>
              <p className="mt-4 text-sm md:text-base text-[var(--maqam-muted)] leading-relaxed">
                Our pilgrims are performing Umrah, insha’Allah. Please keep them in your du‘ā.
              </p>
            </div>
          ) : journeyCompleted ? (
            <div
              data-testid="countdown-completed"
              className="card-elevated rounded-2xl px-8 py-10 md:px-12 md:py-14 text-center max-w-md ml-auto"
            >
              <div className="font-serif text-3xl md:text-4xl text-navy leading-tight">
                Alhamdulillah.
              </div>
              <p className="mt-4 text-sm md:text-base text-[var(--maqam-muted)] leading-relaxed">
                The October 2026 pilgrimage has concluded. May Allah accept every step, every du‘ā, and every act of worship.
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3 md:gap-5 justify-start lg:justify-end">
              <Box value={t.d} label="Days" testid="countdown-days" />
              <Box value={t.h} label="Hours" testid="countdown-hours" />
              <Box value={t.m} label="Minutes" testid="countdown-minutes" />
              <Box value={t.s} label="Seconds" testid="countdown-seconds" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
